import { fleetStopDueDateIso, formatRouteLocation, type FleetRouteStop } from './cartrack';
import { fetchOneMapPostal, fetchOneMapSearchCoords, haversineKm } from './demDispatchGeocode';
import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';
import type { Task } from '../types/crm';
import { singaporeDateStringFromIso } from '../utils/dateTime';

const CLOSED_TASK_STATUSES = new Set(['done', 'completed', 'cancelled']);
/** Slightly lower so area/mall names can pair when a few tokens overlap stop address. */
const AUTO_ASSIGN_MIN_SCORE = 0.48;
const STOP_PROXIMITY_KM = 0.35;
const GEOCODE_DELAY_MS = 150;
const COORD_ADDRESS_RE = /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/;

const ADDRESS_STOP_WORDS = new Set([
  'pte',
  'ltd',
  'limited',
  'singapore',
  'the',
  'and',
  'hub',
  'centre',
  'center',
  'building',
  'blk',
  'block',
  'cluster',
]);

export type FleetTaskGeoCoords = Record<string, { lat: number; lng: number }>;

export function fleetStopKey(stop: FleetRouteStop): string {
  return `${stop.startedAt}|${stop.endedAt}`;
}

const FLEET_STOP_DESC_RE = /\n?\[fleet-stop:([^\]]+)\]\s*$/;

export function fleetStopKeyFromTask(task: Task): string | null {
  const direct = task.fleet_stop_key?.trim();
  if (direct) return direct;
  const match = FLEET_STOP_DESC_RE.exec(task.description ?? '');
  return match?.[1]?.trim() || null;
}

export function fleetTaskDescriptionForDisplay(description?: string | null): string {
  return (description ?? '').replace(FLEET_STOP_DESC_RE, '').trim();
}

function withFleetStopDescription(description: string | null | undefined, key: string): string {
  const base = fleetTaskDescriptionForDisplay(description);
  const marker = `[fleet-stop:${key}]`;
  return base ? `${base}\n${marker}` : marker;
}

function isMissingFleetStopKeyColumn(error: unknown): boolean {
  const message = formatSupabaseError(error);
  return /fleet_stop_key/i.test(message) && /(column|schema cache|does not exist|PGRST204)/i.test(message);
}

function safeStopAddress(stop: FleetRouteStop, task: Task): string {
  const geocoded = stop.address?.trim();
  if (geocoded) return geocoded;
  const taskAddress = task.address?.trim();
  if (taskAddress) return taskAddress;
  if (Number.isFinite(stop.lat) && Number.isFinite(stop.lng)) {
    return formatRouteLocation(stop.lat, stop.lng);
  }
  return 'Unknown location';
}

export function isOpenFleetTask(task: Task): boolean {
  const status = (task.status ?? '').trim().toLowerCase();
  return !CLOSED_TASK_STATUSES.has(status);
}

/** Address + outlet/area — both used for Fleet auto-pair. */
export function fleetTaskLocationText(task: Task): string {
  const address = task.address?.trim() ?? '';
  const area = task.outlet_name?.trim() ?? '';
  if (address && area) {
    const addrNorm = normalizeAddressText(address);
    const areaNorm = normalizeAddressText(area);
    if (addrNorm.includes(areaNorm) || areaNorm.includes(addrNorm)) return address;
    return `${address} ${area}`;
  }
  return address || area;
}

export function fleetTaskPostal(task: Task): string | null {
  return extractPostalCode(fleetTaskLocationText(task));
}

export function isFleetTaskAssignedToStop(task: Task): boolean {
  return Boolean(fleetStopKeyFromTask(task));
}

export function isFleetTaskAssignedToThisStop(task: Task, stop: FleetRouteStop): boolean {
  const key = fleetStopKeyFromTask(task);
  if (!key) return false;
  return key === fleetStopKey(stop);
}

function normalizeAddressText(value: string): string {
  return value
    .toLowerCase()
    .replace(/singapore/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractPostalCode(value: string): string | null {
  const match = value.match(/\b(\d{6})\b/);
  return match?.[1] ?? null;
}

function addressTokens(value: string): Set<string> {
  return new Set(
    normalizeAddressText(value)
      .split(' ')
      .filter((token) => token.length > 2 && !ADDRESS_STOP_WORDS.has(token)),
  );
}

export function addressMatchScore(taskText: string, stopText: string): number {
  const left = normalizeAddressText(taskText);
  const right = normalizeAddressText(stopText);
  if (!left || !right) return 0;
  if (left === right) return 1;
  if (left.includes(right) || right.includes(left)) return 0.95;

  const postalLeft = extractPostalCode(taskText);
  const postalRight = extractPostalCode(stopText);
  if (postalLeft && postalRight && postalLeft === postalRight) return 0.92;

  const blockRoadMatch = taskText.match(/(?:@|\b)(\d{1,4})\s+([A-Za-z][A-Za-z\s]{2,})/);
  if (blockRoadMatch) {
    const block = blockRoadMatch[1]!;
    const road = normalizeAddressText(blockRoadMatch[2]!);
    if (right.includes(block) && road.split(' ').some((part) => part.length > 2 && right.includes(part))) {
      return 0.88;
    }
  }

  const tokensLeft = addressTokens(taskText);
  const tokensRight = addressTokens(stopText);
  if (tokensLeft.size === 0 || tokensRight.size === 0) return 0;

  let shared = 0;
  for (const token of tokensLeft) {
    if (tokensRight.has(token)) shared += 1;
  }
  const union = new Set([...tokensLeft, ...tokensRight]).size;
  const jaccard = union > 0 ? shared / union : 0;

  // Area/mall-only labels are short — if most of their tokens appear in the stop address, accept.
  if (tokensLeft.size <= 4 && shared >= Math.min(2, tokensLeft.size) && shared / tokensLeft.size >= 0.66) {
    return Math.max(jaccard, 0.72);
  }

  return jaccard;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function buildFleetTaskGeoCoords(tasks: Task[]): Promise<FleetTaskGeoCoords> {
  const byPostal = new Map<string, string[]>();
  const searchQueries = new Map<string, string[]>();

  for (const task of tasks) {
    const postal = fleetTaskPostal(task);
    if (postal) {
      const ids = byPostal.get(postal) ?? [];
      ids.push(task.id);
      byPostal.set(postal, ids);
      continue;
    }

    const area = task.outlet_name?.trim() || '';
    const address = task.address?.trim() || '';
    const query = area || address;
    if (!query || query.length < 3) continue;
    const ids = searchQueries.get(query) ?? [];
    ids.push(task.id);
    searchQueries.set(query, ids);
  }

  const result: FleetTaskGeoCoords = {};

  for (const [postal, ids] of byPostal) {
    const lookup = await fetchOneMapPostal(postal);
    if (lookup) {
      for (const id of ids) {
        result[id] = lookup;
      }
    }
    await sleep(GEOCODE_DELAY_MS);
  }

  for (const [query, ids] of searchQueries) {
    const lookup = await fetchOneMapSearchCoords(query);
    if (lookup) {
      for (const id of ids) {
        if (!result[id]) result[id] = lookup;
      }
    }
    await sleep(GEOCODE_DELAY_MS);
  }

  return result;
}

function taskGeoCoords(task: Task, geoCoords?: FleetTaskGeoCoords): { lat: number; lng: number } | null {
  return geoCoords?.[task.id] ?? null;
}

function coordsFromLocationText(text: string): { lat: number; lng: number } | null {
  const match = text.trim().match(COORD_ADDRESS_RE);
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function taskCoordsNearStop(
  task: Task,
  stop: FleetRouteStop,
  geoCoords?: FleetTaskGeoCoords,
): boolean {
  const cached = taskGeoCoords(task, geoCoords);
  if (cached && haversineKm(cached.lat, cached.lng, stop.lat, stop.lng) <= STOP_PROXIMITY_KM) {
    return true;
  }
  const fromAddress = coordsFromLocationText(fleetTaskLocationText(task));
  if (!fromAddress) return false;
  return haversineKm(fromAddress.lat, fromAddress.lng, stop.lat, stop.lng) <= STOP_PROXIMITY_KM;
}

function stopMatchScoreForTask(
  task: Task,
  stop: FleetRouteStop,
  geoCoords?: FleetTaskGeoCoords,
): number {
  const locationText = fleetTaskLocationText(task);
  const stopAddress = stop.address?.trim() ?? '';
  const areaOnly = task.outlet_name?.trim() ?? '';

  let textScore = 0;
  if (locationText && stopAddress) {
    textScore = addressMatchScore(locationText, stopAddress);
  }
  // Also score area label alone (mall / MRT) against reverse-geocoded stop text.
  if (areaOnly && stopAddress && areaOnly !== locationText) {
    textScore = Math.max(textScore, addressMatchScore(areaOnly, stopAddress));
  }

  const geoScore = taskCoordsNearStop(task, stop, geoCoords) ? 0.86 : 0;
  return Math.max(textScore, geoScore);
}

export function findBestStopForTask(
  task: Task,
  stops: FleetRouteStop[],
  geoCoords?: FleetTaskGeoCoords,
): { stop: FleetRouteStop; score: number } | null {
  let best: { stop: FleetRouteStop; score: number } | null = null;

  for (const stop of stops) {
    const score = stopMatchScoreForTask(task, stop, geoCoords);
    if (!best || score > best.score) {
      best = { stop, score };
    }
  }

  if (!best || best.score < AUTO_ASSIGN_MIN_SCORE) return null;
  return best;
}

function isDoneFleetTask(task: Task): boolean {
  const status = (task.status ?? '').trim().toLowerCase();
  return status === 'done' || status === 'completed';
}

function isCancelledFleetTask(task: Task): boolean {
  return (task.status ?? '').trim().toLowerCase() === 'cancelled';
}

export function isFleetTaskOnRouteDate(task: Task, routeDate: string): boolean {
  if (task.due_date && singaporeDateStringFromIso(task.due_date) === routeDate) return true;
  if (isDoneFleetTask(task) && task.created_at && singaporeDateStringFromIso(task.created_at) === routeDate) {
    return true;
  }
  return false;
}

function isFleetTaskLocationMatchedToStop(
  task: Task,
  stop: FleetRouteStop,
  geoCoords?: FleetTaskGeoCoords,
): boolean {
  return stopMatchScoreForTask(task, stop, geoCoords) >= AUTO_ASSIGN_MIN_SCORE;
}

export function fleetTasksForStop(
  tasks: Task[],
  routeDate: string,
  stop: FleetRouteStop,
  geoCoords?: FleetTaskGeoCoords,
): Task[] {
  const stopKey = fleetStopKey(stop);

  return tasks
    .filter((task) => {
      if (isCancelledFleetTask(task)) return false;
      if (!isFleetTaskOnRouteDate(task, routeDate)) return false;

      const assignedKey = fleetStopKeyFromTask(task);
      if (assignedKey) {
        return assignedKey === stopKey;
      }

      if (!isOpenFleetTask(task)) return false;
      return isFleetTaskLocationMatchedToStop(task, stop, geoCoords);
    })
    .sort((a, b) => {
      const aDone = isDoneFleetTask(a);
      const bDone = isDoneFleetTask(b);
      if (aDone !== bDone) return aDone ? 1 : -1;
      return a.title.localeCompare(b.title);
    });
}

/** Tasks due on the selected route day that are not yet linked to a fleet stop (open or done). */
export function fleetTasksAssignableToStop(
  tasks: Task[],
  routeDate: string,
  _stops?: FleetRouteStop[],
  _targetStop?: FleetRouteStop | null,
): Task[] {
  return tasks
    .filter((task) => {
      if (isCancelledFleetTask(task)) return false;
      if (!routeDate) return false;
      // Strict: due on this route date only (do not match Done via created_at).
      if (!task.due_date || singaporeDateStringFromIso(task.due_date) !== routeDate) return false;
      if (isFleetTaskAssignedToStop(task)) return false;
      return true;
    })
    .sort((a, b) => {
      const ownerCmp = (a.owner_id ?? '').localeCompare(b.owner_id ?? '', undefined, {
        sensitivity: 'base',
      });
      if (ownerCmp !== 0) return ownerCmp;
      const aDone = isDoneFleetTask(a);
      const bDone = isDoneFleetTask(b);
      if (aDone !== bDone) return aDone ? 1 : -1;
      const aDue = a.due_date ? Date.parse(a.due_date) : 0;
      const bDue = b.due_date ? Date.parse(b.due_date) : 0;
      if (aDue !== bDue) return aDue - bDue;
      return a.title.localeCompare(b.title);
    });
}

export function fleetTasksForAutoAssign(
  tasks: Task[],
  routeDate: string,
  stops: FleetRouteStop[],
  geoCoords?: FleetTaskGeoCoords,
): Task[] {
  return tasks.filter((task) => {
    if (!isFleetTaskOnRouteDate(task, routeDate)) return false;
    if (!isOpenFleetTask(task)) return false;
    if (isFleetTaskAssignedToStop(task)) return false;

    const locationText = fleetTaskLocationText(task);
    if (!locationText && !taskGeoCoords(task, geoCoords)) return false;

    return findBestStopForTask(task, stops, geoCoords) != null;
  });
}

export function canAutoAssignFleetTasks(
  stops: FleetRouteStop[],
  geoCoords?: FleetTaskGeoCoords,
): boolean {
  if (stops.some((stop) => stop.address?.trim())) return true;
  return Boolean(geoCoords && Object.keys(geoCoords).length > 0);
}

export async function assignFleetTaskToStop(
  task: Task,
  stop: FleetRouteStop,
  _routeDate: string,
): Promise<Task> {
  const due_date = fleetStopDueDateIso(stop.startedAt);
  const address = safeStopAddress(stop, task);
  const fleet_stop_key = fleetStopKey(stop);
  const description = withFleetStopDescription(task.description, fleet_stop_key);

  const updateTask = async (payload: Record<string, unknown>): Promise<Task> => {
    const { data, error } = await getDbSupabase()
      .from('tasks')
      .update(payload)
      .eq('id', task.id)
      .select('*')
      .single();

    if (error) throw error;
    return data as Task;
  };

  try {
    return await updateTask({ due_date, address, fleet_stop_key });
  } catch (error) {
    if (!isMissingFleetStopKeyColumn(error)) {
      throw new Error(formatSupabaseError(error));
    }
    try {
      return await updateTask({ due_date, address, description });
    } catch (fallbackError) {
      throw new Error(formatSupabaseError(fallbackError));
    }
  }
}

export async function autoAssignFleetTasksToStops(
  tasks: Task[],
  routeDate: string,
  stops: FleetRouteStop[],
  geoCoords?: FleetTaskGeoCoords,
): Promise<Task[]> {
  const assigned: Task[] = [];

  for (const task of fleetTasksForAutoAssign(tasks, routeDate, stops, geoCoords)) {
    const match = findBestStopForTask(task, stops, geoCoords);
    if (!match) continue;
    const updated = await assignFleetTaskToStop(task, match.stop, routeDate);
    assigned.push(updated);
  }

  return assigned;
}
